import { useState } from "react";
import {
  Alert,
  FormModal,
  FormSectionLabel,
  Input,
  ResponsiveGrid,
  Select,
  Textarea,
} from "@marekl/adminer/components";
import {
  floorLabel,
  MAX_TECH_JUDGES,
  useCreateDiscipline,
  useUpdateDiscipline,
  type Discipline,
  type DisciplinePayload,
  type MusicSource,
  type ResultFormula,
  type RuleSetWorkspace,
} from "../../lib/queries/rules";
import {
  MUSIC_SOURCE_LABELS,
  RESULT_FORMULA_LABELS,
  formatSeconds,
  parseTimeInput,
} from "../../lib/rulesLabels";
import type { ID } from "../../lib/id";

/**
 * Create/edit form for a discipline, shared by the rule-set workspace
 * and the discipline detail page so the two cannot drift apart.
 *
 * Pass `discipline` to edit, omit it to create. The fields are seeded
 * once on mount, so give the element a `key` that changes with the
 * subject (`key={discipline?.id ?? "new"}`) to reseed it.
 */
interface Form {
  family: string;
  code: string;
  name: string;
  description: string;
  apparatus: string;
  music_source: MusicSource;
  restrictions: string;
  age_category_table_id: string;
  formation_table_id: string;
  performance_class_table_id: string;
  default_deduction_table_id: string;
  default_scoring_sheet_id: string;
  time_min: string;
  time_max: string;
  default_floor_id: string;
  default_recording_id: string;
  plus_judges: string;
  tech_judges: string;
  result_formula: ResultFormula;
}

const EMPTY: Form = {
  family: "",
  code: "",
  name: "",
  description: "",
  apparatus: "",
  music_source: "own",
  restrictions: "",
  age_category_table_id: "",
  formation_table_id: "",
  performance_class_table_id: "",
  default_deduction_table_id: "",
  default_scoring_sheet_id: "",
  time_min: "",
  time_max: "",
  default_floor_id: "",
  default_recording_id: "",
  plus_judges: "3",
  tech_judges: "1",
  result_formula: "average",
};

function formFrom(d: Discipline): Form {
  return {
    family: d.family,
    code: d.code,
    name: d.name,
    description: d.description,
    apparatus: d.apparatus,
    music_source: d.music_source,
    restrictions: d.restrictions,
    age_category_table_id: String(d.age_category_table_id),
    formation_table_id: String(d.formation_table_id),
    performance_class_table_id: String(d.performance_class_table_id),
    default_deduction_table_id: d.default_deduction_table_id
      ? String(d.default_deduction_table_id)
      : "",
    default_scoring_sheet_id: d.default_scoring_sheet_id
      ? String(d.default_scoring_sheet_id)
      : "",
    time_min:
      d.default_time_min_seconds === undefined ? "" : formatSeconds(d.default_time_min_seconds),
    time_max:
      d.default_time_max_seconds === undefined ? "" : formatSeconds(d.default_time_max_seconds),
    default_floor_id: d.default_floor_id ? String(d.default_floor_id) : "",
    default_recording_id: d.default_recording_id ? String(d.default_recording_id) : "",
    plus_judges: String(d.default_plus_judges),
    tech_judges: String(d.default_tech_judges),
    result_formula: d.default_result_formula,
  };
}

const idOrNull = (value: string): ID | null => (value ? value : null);

function toPayload(f: Form): DisciplinePayload {
  return {
    family: f.family.trim(),
    code: f.code.trim(),
    name: f.name.trim(),
    description: f.description.trim(),
    apparatus: f.apparatus.trim(),
    music_source: f.music_source,
    restrictions: f.restrictions.trim(),
    age_category_table_id: f.age_category_table_id,
    formation_table_id: f.formation_table_id,
    performance_class_table_id: f.performance_class_table_id,
    default_deduction_table_id: idOrNull(f.default_deduction_table_id),
    default_scoring_sheet_id: idOrNull(f.default_scoring_sheet_id),
    default_time_min_seconds: parseTimeInput(f.time_min),
    default_time_max_seconds: parseTimeInput(f.time_max),
    default_floor_id: idOrNull(f.default_floor_id),
    default_recording_id: idOrNull(f.default_recording_id),
    default_plus_judges: Number(f.plus_judges),
    default_tech_judges: Number(f.tech_judges),
    default_result_formula: f.result_formula,
  };
}

export function DisciplineFormModal({
  open,
  workspace,
  discipline,
  structureLocked,
  onClose,
}: {
  open: boolean;
  workspace: RuleSetWorkspace;
  discipline?: Discipline;
  /**
   * The rule set is in service, so the three axis tables are frozen: they
   * decide WHICH combinations exist and clubs are already entering against
   * them. The modal stays open and everything else stays editable — a wrong
   * time limit must remain fixable mid-season.
   */
  structureLocked?: boolean;
  onClose: () => void;
}) {
  const {
    age_tables,
    formation_tables,
    performance_class_tables,
    deduction_tables,
    scoring_sheets,
  } = workspace;
  const [form, setForm] = useState<Form>(() =>
    discipline
      ? formFrom(discipline)
      : {
          ...EMPTY,
          age_category_table_id: age_tables[0] ? String(age_tables[0].id) : "",
          formation_table_id: formation_tables[0] ? String(formation_tables[0].id) : "",
          performance_class_table_id: performance_class_tables[0]
            ? String(performance_class_tables[0].id)
            : "",
        },
  );

  const create = useCreateDiscipline(workspace.ruleset.id);
  const update = useUpdateDiscipline();

  const close = () => {
    create.reset();
    update.reset();
    onClose();
  };

  const submit = () => {
    const payload = toPayload(form);
    if (discipline) {
      update.mutate({ id: discipline.id, payload }, { onSuccess: close });
    } else {
      create.mutate(payload, { onSuccess: close });
    }
  };

  // Swapping an axis regenerates the matrix; combinations whose row or
  // column disappears are dropped along with anything set on them.
  const tablesChanged =
    !!discipline &&
    (String(discipline.age_category_table_id) !== form.age_category_table_id ||
      String(discipline.formation_table_id) !== form.formation_table_id ||
      String(discipline.performance_class_table_id) !== form.performance_class_table_id);

  const invalid =
    !form.family.trim() ||
    !form.code.trim() ||
    !form.name.trim() ||
    !form.age_category_table_id ||
    !form.formation_table_id ||
    !form.performance_class_table_id ||
    !/^\d+$/.test(form.plus_judges.trim()) ||
    Number(form.plus_judges) < 1 ||
    !/^\d+$/.test(form.tech_judges.trim()) ||
    Number(form.tech_judges) > MAX_TECH_JUDGES ||
    (form.time_min.trim() !== "" && parseTimeInput(form.time_min) === null) ||
    (form.time_max.trim() !== "" && parseTimeInput(form.time_max) === null);

  return (
    <FormModal
      open={open}
      title={discipline ? "Upravit disciplínu" : "Nová disciplína"}
      submitLabel="Uložit"
      busy={create.isPending || update.isPending}
      canSubmit={!invalid}
      error={create.error || update.error}
      onSubmit={submit}
      onClose={close}
      width="lg"
    >
      {structureLocked && (
        <Alert variant="warn">
          Sada se používá na zveřejněné soutěži, takže tabulky os už měnit nelze:
          rozhodují o tom, které kombinace existují, a týmy se podle nich hlásí.
          Ostatní údaje upravovat můžete.
        </Alert>
      )}
      {tablesChanged && (
        <Alert variant="warn">
          Změnou tabulky se kombinace disciplíny přegenerují. Kombinace, jejichž věková
          kategorie nebo formace v nové tabulce není, zaniknou, i s vlastním nastavením,
          které na nich bylo.
        </Alert>
      )}
      <ResponsiveGrid columns={2}>
        <Input
          label="Skupina"
          required
          placeholder="MD"
          value={form.family}
          onChange={(e) => setForm({ ...form, family: e.target.value })}
          hint="Slouží jen k seskupení v přehledu."
        />
        <Input
          label="Kód"
          required
          placeholder="MD-DANCE"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
      </ResponsiveGrid>
      <Input
        label="Název"
        required
        placeholder="MD Dance"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <Textarea
        label="Popis"
        rows={2}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
      />

      <FormSectionLabel>Osy kombinací</FormSectionLabel>
      <ResponsiveGrid columns={2}>
        <Select
          label="Tabulka věkových kategorií"
          required
          disabled={structureLocked}
          value={form.age_category_table_id}
          onChange={(e) => setForm({ ...form, age_category_table_id: e.target.value })}
        >
          <option value="">— vyberte —</option>
          {age_tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Select
          label="Tabulka formací"
          required
          disabled={structureLocked}
          value={form.formation_table_id}
          onChange={(e) => setForm({ ...form, formation_table_id: e.target.value })}
        >
          <option value="">— vyberte —</option>
          {formation_tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>
      <Select
        label="Tabulka výkonnostních tříd"
        required
        disabled={structureLocked}
        value={form.performance_class_table_id}
        onChange={(e) => setForm({ ...form, performance_class_table_id: e.target.value })}
        hint="Disciplína soutěžená v jediné třídě použije tabulku s jedním řádkem."
      >
        <option value="">— vyberte —</option>
        {performance_class_tables.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>

      <FormSectionLabel>Výchozí nastavení kombinací</FormSectionLabel>
      <ResponsiveGrid columns={2}>
        <Input
          label="Nejkratší čas"
          placeholder="1:15"
          value={form.time_min}
          onChange={(e) => setForm({ ...form, time_min: e.target.value })}
          hint="Formát m:ss. 0:00 = bez minima, prázdné = nenastaveno."
        />
        <Input
          label="Nejdelší čas"
          placeholder="1:30"
          value={form.time_max}
          onChange={(e) => setForm({ ...form, time_max: e.target.value })}
          hint="Shodný s nejkratším = pevná délka."
        />
      </ResponsiveGrid>
      <ResponsiveGrid columns={2}>
        <Select
          label="Soutěžní plocha"
          value={form.default_floor_id}
          onChange={(e) => setForm({ ...form, default_floor_id: e.target.value })}
          hint={
            workspace.floors.length === 0
              ? "Zatím není zadaná žádná velikost. Přidejte ji na kartě „Soutěžní plochy“."
              : undefined
          }
        >
          <option value="">— nenastaveno —</option>
          {workspace.floors.map((f) => (
            <option key={f.id} value={f.id}>
              {floorLabel(f)}
            </option>
          ))}
        </Select>
        <Input
          label="Náčiní"
          placeholder="Hůlka (1 ks)"
          value={form.apparatus}
          onChange={(e) => setForm({ ...form, apparatus: e.target.value })}
        />
      </ResponsiveGrid>
      <ResponsiveGrid columns={3}>
        <Input
          label="Plusových porotců"
          required
          type="number"
          min={1}
          value={form.plus_judges}
          onChange={(e) => setForm({ ...form, plus_judges: e.target.value })}
        />
        <Input
          label="Technických porotců"
          required
          type="number"
          min={0}
          max={MAX_TECH_JUDGES}
          hint="Zatím nejvýš jeden."
          value={form.tech_judges}
          onChange={(e) => setForm({ ...form, tech_judges: e.target.value })}
        />
        <Select
          label="Výsledek"
          value={form.result_formula}
          onChange={(e) =>
            setForm({ ...form, result_formula: e.target.value as ResultFormula })
          }
        >
          {Object.entries(RESULT_FORMULA_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>
      <ResponsiveGrid columns={2}>
        <Select
          label="Srážková tabulka"
          value={form.default_deduction_table_id}
          onChange={(e) => setForm({ ...form, default_deduction_table_id: e.target.value })}
        >
          <option value="">— nenastaveno —</option>
          {deduction_tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Select
          label="Hodnoticí tabulka"
          value={form.default_scoring_sheet_id}
          onChange={(e) => setForm({ ...form, default_scoring_sheet_id: e.target.value })}
        >
          <option value="">— nenastaveno —</option>
          {scoring_sheets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>

      <FormSectionLabel>Ostatní</FormSectionLabel>
      <ResponsiveGrid columns={2}>
        <Select
          label="Hudba"
          value={form.music_source}
          onChange={(e) => {
            // Clearing the track on the way out matters: the picker
            // below is only rendered for `federation`, so a value left
            // behind here is a reference nothing on screen can show
            // and nobody can undo — and it makes the recording
            // undeletable with a 409 that names no culprit.
            const music_source = e.target.value as MusicSource;
            setForm({
              ...form,
              music_source,
              default_recording_id:
                music_source === "federation" ? form.default_recording_id : "",
            });
          }}
        >
          {Object.entries(MUSIC_SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        {/* Only the federation source has a track to choose. The
            combinations that depart from it — TD Twirl's tým, danced
            to a longer recording — override this in the matrix. */}
        {form.music_source === "federation" && (
          <Select
            label="Výchozí nahrávka"
            value={form.default_recording_id}
            onChange={(e) => setForm({ ...form, default_recording_id: e.target.value })}
            hint={
              workspace.recordings.length === 0
                ? "Zatím není nahraná žádná nahrávka. Přidejte ji na kartě Hudba."
                : "U kombinace lze nastavit jinou."
            }
          >
            <option value="">— nenastaveno —</option>
            {workspace.recordings.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        )}
      </ResponsiveGrid>
      <Textarea
        label="Omezení"
        rows={3}
        value={form.restrictions}
        onChange={(e) => setForm({ ...form, restrictions: e.target.value })}
        hint="Volný text: omezení prvků, povinné části a podobně."
      />
    </FormModal>
  );
}
